import React, { useState, useEffect, useRef, useCallback } from 'react'
import { PlanPositionRow, Okei, PurchaseItemRowErrors } from '../types'
import { Okpd2 } from '../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../Org/Home/types'

import Okpd2Dropdown from './Okpd2Dropdown'

import HoverTooltip from '../../../../../../components/HoverTooltip'

import FixedDropdown from './FixedDropdown'

type LayoutProps = {
    index: number
    orgData: OrganizationInfo | null | undefined
    positionRow: PlanPositionRow
    showRegion: boolean
    theme: string
    onNameChanged: (value: string) => void
    onImpossibleToDetermineAttrChanged: (
        attr: boolean | null,
        value_qty: number | null,
        code_okei: string | null,
        value_okei: string | null
    ) => void
    onCountChanged: (value: number | null) => void
    onOkeiChanged: (code: string | null, value: string | null) => void
    onTypePurchaseChanged: (value: string) => void
    onDropdownSelected: (type: string) => void
    onDeleteClick: () => void
    okeiList: Okei[]
    disabledFieldset: boolean
    selected: boolean
    onClick: (row_guid: string) => void
    onCopy: (row_guid: string) => void
    errors?: PurchaseItemRowErrors
    isMspSecondYear: boolean
    plannedAfterSecondYear: boolean
    onPickOkpd2: (rowIndex: number, item: Okpd2) => void
    onPickOkved2: (
        rowIndex: number,
        item: { code: string; name: string }
    ) => void
    posMSPStatus: boolean | null
    purchaseMethodCode: number | null
    isFirstSelected: boolean
    rowsCount: number
    onHaveNotMspOkpd2: (value: boolean) => void
}

const PurchaseItemsTableRow: React.FC<LayoutProps> = ({
    index,
    orgData,
    positionRow,
    showRegion,
    theme,
    onNameChanged,
    onImpossibleToDetermineAttrChanged,
    onCountChanged,
    onOkeiChanged,
    onTypePurchaseChanged,
    onDropdownSelected,
    onDeleteClick,
    okeiList,
    disabledFieldset,
    selected,
    onClick,
    onCopy,
    errors,
    isMspSecondYear,
    plannedAfterSecondYear,
    onPickOkpd2,
    onPickOkved2,
    posMSPStatus,
    purchaseMethodCode,
    isFirstSelected,
    rowsCount,
    onHaveNotMspOkpd2,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [text, setText] = useState('')
    const [dataSearchOkpd2, setDataSearchOkpd2] = useState<Okpd2 | null>(null)

    const [okpd2Query, setOkpd2Query] = useState(positionRow.okpd2_code ?? '')
    const [okved2Query, setOkved2Query] = useState(
        positionRow.okved2_code ?? ''
    )

    const [okpd2Open, setOkpd2Open] = useState(false)
    const [okved2Open, setOkved2Open] = useState(false)

    const [okpd2Items, setOkpd2Items] = useState<Okpd2[]>([])
    const [okved2Items, setOkved2Items] = useState<
        Array<{ code: string; name: string }>
    >([])

    const okpd2DebRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const okved2DebRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const okpd2InputRef = useRef<HTMLInputElement>(null)

    const okpd2PrevRef = useRef<string>('') // что было до открытия
    const okpd2PickedRef = useRef<boolean>(false) // выбрали ли пункт

    const okved2InputRef = useRef<HTMLInputElement>(null)

    const okved2PrevRef = useRef<string>('') // что было до открытия
    const okved2PickedRef = useRef<boolean>(false) // выбрали ли пункт

    const isDisabledSelect =
        plannedAfterSecondYear ||
        isMspSecondYear === true ||
        positionRow.impossible_to_determine_attr

    const fetchOkpd2 = async (q: string) => {
        if (!q?.trim()) {
            setOkpd2Items([])
            return
        }
        const isCode = /^[0-9]/.test(q.trim())
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/okpd2/${isCode ? 'search_code' : 'search_name'}?text_query=${encodeURIComponent(q.trim())}`
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) return
        const data: Okpd2[] = await res.json()
        setOkpd2Items(data)
    }

    const fetchOkved2 = async (q: string) => {
        if (!q?.trim()) {
            setOkved2Items([])
            return
        }
        const isCode = /^[0-9]/.test(q.trim())
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/okved2/${isCode ? 'search_code' : 'search_name'}?text_query=${encodeURIComponent(q.trim())}`
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) return
        const data = (await res.json()) as Array<{ code: string; name: string }>
        setOkved2Items(data.slice(0, 15))
    }

    const onOkpd2Input = (v: string) => {
        setOkpd2Query(v)
        if (okpd2DebRef.current) clearTimeout(okpd2DebRef.current)
        okpd2DebRef.current = setTimeout(() => {
            fetchOkpd2(v)
            setOkpd2Open(true)
            setOkved2Open(false) // важно
        }, 500)
    }

    const onOkved2Input = (v: string) => {
        setOkved2Query(v)
        if (okved2DebRef.current) clearTimeout(okved2DebRef.current)
        okved2DebRef.current = setTimeout(() => {
            fetchOkved2(v)
            setOkved2Open(true)
            setOkpd2Open(false) // важно
        }, 500)
    }

    const pickOkpd2 = (item: Okpd2) => {
        setOkpd2Open(false)
        setOkved2Open(false) // <--- добавь
        setOkpd2Query(item.code)
        onPickOkpd2(index, item)
    }

    const pickOkved2 = (item: { code: string; name: string }) => {
        setOkved2Open(false)
        setOkpd2Open(false) // <--- добавь
        setOkved2Query(item.code)
        onPickOkved2(index, item)
    }

    useEffect(() => {
        setOkpd2Query(positionRow.okpd2_code ?? '')
    }, [positionRow.okpd2_code])

    useEffect(() => {
        setOkved2Query(positionRow.okved2_code ?? '')
    }, [positionRow.okved2_code])

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            const t = e.target as HTMLElement
            if (!t.closest('[data-autocomplete="okpd2"]')) setOkpd2Open(false)
            if (!t.closest('[data-autocomplete="okved2"]')) setOkved2Open(false)
        }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [])

    const isCodeAllLetters = (code?: string) => !!code && /^[A-Z]+$/.test(code)

    const canPickOkpd2Inline = (item: any) => {
        // если код "служебный" (все буквы) — нельзя
        if (isCodeAllLetters(item.code)) return false

        // если организация разрешает игнорировать ограничения МСП
        if (orgData?.ignore_msp_okpd2) {
            // когда all_okpd2 === true — разрешаем только "листья"
            if (orgData?.all_okpd2) return !!item.is_leaf
            // когда all_okpd2 === false — разрешаем всё
            return true
        }

        // если "все способы закупки" выключены (all_okpd2 === false)
        if (orgData?.all_okpd2 === false) {
            // если способ = 0/null и ещё не выбрали первый код — можно
            if (
                (purchaseMethodCode === 0 || purchaseMethodCode == null) &&
                !isFirstSelected
            )
                return true
            // иначе — только коды, совпадающие по МСП‑статусу
            return item.is_msp === posMSPStatus
        }

        // all_okpd2 === true
        // выбирать можно только "листья"
        if (!item.is_leaf) return false

        // если способ = 0/null, первый ещё не выбран и есть хотя бы одна строка — можно
        if (
            (purchaseMethodCode === 0 || purchaseMethodCode == null) &&
            !isFirstSelected &&
            rowsCount > 0
        )
            return true

        // иначе — только коды, совпадающие по МСП‑статусу
        return item.is_msp === posMSPStatus
    }

    const [localValue, setLocalValue] = useState(
        positionRow.qty != null
            ? Number(positionRow.qty).toLocaleString('ru-RU', {
                  useGrouping: false,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 13,
              })
            : ''
    )

    // Сохраняем идентификатор таймера для debounce
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    )

    // Функция для запроса данных OKPD2
    const searchOkpd2 = async (codeOkpd2: string) => {
        if (!codeOkpd2) {
            setDataSearchOkpd2(null)
            return
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/okpd2/search_code?text_query=${codeOkpd2}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (response.ok) {
                const data: Okpd2[] = await response.json()
                if (data.length > 0) {
                    setDataSearchOkpd2(data[0]) // Берем первый результат

                    if (data[0].is_msp == false) {
                        // Если ОКПД2 не МСП, то способ МСП нельзя
                        onHaveNotMspOkpd2(true)
                    }
                } else {
                    setDataSearchOkpd2(null)
                }
            } else {
                throw new Error('Ошибка запроса на поиск OKPD2')
            }
        } catch (error) {
            console.error(error)
            setDataSearchOkpd2(null)
        }
    }

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto' // Сбрасываем высоту перед пересчетом
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`
        }
    }, [text]) // Вызываем при изменении текста

    // Вызываем searchOkpd2 при монтировании компонента и при изменении positionRow.okpd2_code
    useEffect(() => {
        searchOkpd2(positionRow.okpd2_code ?? '')
    }, [positionRow.okpd2_code])

    // Если родитель изменяет значение, синхронизируем локальное состояние
    useEffect(() => {
        if (positionRow.qty != null) {
            setLocalValue(
                Number(positionRow.qty).toLocaleString('ru-RU', {
                    useGrouping: false,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 13,
                })
            )
        }
    }, [positionRow.qty])

    // Функция, которая вызывается с задержкой 500 мс после последнего изменения ввода
    const debouncedUpdate = useCallback(
        (value: string) => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }
            debounceTimeoutRef.current = setTimeout(() => {
                // Заменяем запятую на точку и пробуем распарсить число
                const parsed = parseFloat(value.replace(',', '.'))
                if (!isNaN(parsed)) {
                    // Ограничиваем число до 4 знаков после запятой
                    const rounded = Number(parsed.toFixed(4))
                    // Форматируем число согласно локали
                    const formatted = rounded.toLocaleString('ru-RU', {
                        useGrouping: false,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 13,
                    })
                    setLocalValue(formatted)
                    onCountChanged(rounded)
                } else {
                    setLocalValue('')
                    onCountChanged(0)
                }
            }, 500)
        },
        [onCountChanged]
    )

    const handleChange = (e: { target: { value: string } }) => {
        const { value } = e.target
        setLocalValue(value)
        debouncedUpdate(value)
    }

    return (
        <div className="flex flex-row gap-1 m-1 text-[11px] rounded items-start justify-start">
            {!disabledFieldset && (
                <div className="flex flex-row h-8 w-8 items-center justify-center text-center">
                    <div
                        className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={() => {
                            onClick(positionRow.guid)
                        }}
                    >
                        <HoverTooltip tooltipText="Выбрать строку">
                            <input
                                type="checkbox"
                                checked={selected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                    onClick(positionRow.guid)
                                }}
                            />
                        </HoverTooltip>
                    </div>
                </div>
            )}

            {!disabledFieldset && (
                <div className="flex flex-row h-8 w-8 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF]">
                    <HoverTooltip tooltipText="Копировать строку">
                        <button
                            onClick={() => onCopy(positionRow.guid)}
                            className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        >
                            <svg
                                className="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="copy"
                            >
                                <path
                                    fill={
                                        theme == 'dark' ? '#FFFFFF' : '#181818'
                                    }
                                    d="M21,8.94a1.31,1.31,0,0,0-.06-.27l0-.09a1.07,1.07,0,0,0-.19-.28h0l-6-6h0a1.07,1.07,0,0,0-.28-.19.32.32,0,0,0-.09,0A.88.88,0,0,0,14.05,2H10A3,3,0,0,0,7,5V6H6A3,3,0,0,0,3,9V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V18h1a3,3,0,0,0,3-3V9S21,9,21,8.94ZM15,5.41,17.59,8H16a1,1,0,0,1-1-1ZM15,19a1,1,0,0,1-1,1H6a1,1,0,0,1-1-1V9A1,1,0,0,1,6,8H7v7a3,3,0,0,0,3,3h5Zm4-4a1,1,0,0,1-1,1H10a1,1,0,0,1-1-1V5a1,1,0,0,1,1-1h3V7a3,3,0,0,0,3,3h3Z"
                                ></path>
                            </svg>
                        </button>
                    </HoverTooltip>
                </div>
            )}

            <div className="flex flex-row h-8 w-8 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF]">
                {index + 1}
            </div>

            <div className="flex flex-1 min-h-8 text-left min-w-64">
                <HoverTooltip
                    tooltipText={
                        errors?.additional_info ? errors?.additional_info : ''
                    }
                >
                    <textarea
                        ref={textareaRef}
                        className={`form-textarea-table-row flex h-8 min-h-8 w-full p-1 px-2 rounded-md text-[12px] text-[#181818] dark:text-[#FFFFFF] bg-[#181818] ${
                            errors?.additional_info
                                ? 'border border-[#C15C5C]'
                                : 'border-0'
                        }`}
                        maxLength={2000}
                        value={positionRow.additional_info ?? ''}
                        onChange={(e) => {
                            onNameChanged(e.target.value)
                            setText(e.target.value)
                        }}
                        onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement
                            target.style.height = 'auto' // Сброс высоты перед обновлением
                            target.style.height = `${target.scrollHeight}px` // Устанавливаем новую высоту
                        }}
                        rows={1}
                    />
                </HoverTooltip>
            </div>

            <fieldset
                className="flex flex-row gap-1"
                disabled={plannedAfterSecondYear || isMspSecondYear == true}
            >
                <div className="flex flex-row h-8 w-8 items-center justify-center text-center">
                    <div
                        className={`flex w-6 h-6 rounded-full items-center justify-center ${disabledFieldset ? '' : 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]'}`}
                    >
                        <HoverTooltip tooltipText="Невозможно определить количество (объем)">
                            <input
                                type="checkbox"
                                className="p-2"
                                checked={
                                    plannedAfterSecondYear ||
                                    isMspSecondYear == true
                                        ? false
                                        : positionRow.impossible_to_determine_attr ||
                                          false
                                }
                                onChange={(e) => {
                                    onImpossibleToDetermineAttrChanged(
                                        e.target.checked,
                                        null,
                                        null,
                                        null
                                    )
                                    setLocalValue('')
                                }}
                                disabled={disabledFieldset}
                            />
                        </HoverTooltip>
                    </div>
                </div>

                <div className="flex flex-row h-[28px] w-28 items-center justify-center text-center">
                    <HoverTooltip tooltipText={errors?.qty ? errors?.qty : ''}>
                        <input
                            type="text"
                            inputMode="numeric"
                            lang="ru"
                            name="purchase_item_count"
                            className={`form-input-table-row rounded-md text-primary text-center w-full h-[28px] pr-1 ${errors?.qty ? 'border border-[#C15C5C]' : 'border-0'}`}
                            style={{
                                colorScheme: theme == 'dark' ? 'dark' : 'light',
                            }}
                            maxLength={18}
                            value={
                                plannedAfterSecondYear ||
                                isMspSecondYear == true ||
                                positionRow.impossible_to_determine_attr
                                    ? ''
                                    : localValue
                            }
                            onChange={handleChange}
                            disabled={
                                positionRow.impossible_to_determine_attr ||
                                false
                            }
                        />
                    </HoverTooltip>
                </div>

                <div className="flex flex-row h-[28px] w-32 items-center justify-center text-center">
                    <HoverTooltip
                        tooltipText={errors?.okei_code ? errors?.okei_code : ''}
                    >
                        <select
                            key={`okei-${positionRow.guid}-${isDisabledSelect ? 'disabled' : 'enabled'}`}
                            name="purchase_item_count_type"
                            className={`form-input-table-row px-2 rounded-md w-full h-[28px] ${errors?.okei_code ? 'border border-[#C15C5C]' : 'border-0'}`}
                            onChange={(e) => {
                                const selectedCode = e.target.value
                                const selectedOkei = okeiList.find(
                                    (okei) => okei.code === selectedCode
                                )
                                onOkeiChanged(
                                    selectedCode,
                                    selectedOkei?.name || null
                                )
                            }}
                            value={
                                isDisabledSelect
                                    ? ''
                                    : (positionRow.okei_code ?? '')
                            }
                            disabled={
                                positionRow.impossible_to_determine_attr ||
                                false
                            }
                        >
                            {isDisabledSelect ? (
                                <option
                                    className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                    value=""
                                ></option> // <-- пусто, когда impossible_to_determine_attr true (и в других твоих кейсах)
                            ) : (
                                <option
                                    className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                    value=""
                                >
                                    Выбрать
                                </option> // <-- обычный плейсхолдер
                            )}
                            {okeiList.map((okei, index_okei) => (
                                <option key={index_okei} value={okei.code}>
                                    {okei.name} ({okei.symbol?.toLowerCase()})
                                </option>
                            ))}
                        </select>
                    </HoverTooltip>
                </div>
            </fieldset>

            <fieldset
                className="flex flex-row gap-1"
                disabled={plannedAfterSecondYear}
            >
                <div className="flex flex-row h-[28px] w-28 items-center justify-center text-center text-[#181818] dark:text-[#FFFFFF]">
                    <HoverTooltip
                        tooltipText={
                            errors?.type_object_purchase
                                ? errors?.type_object_purchase
                                : ''
                        }
                    >
                        <select
                            name="purchase_item_type_purchase"
                            className={`form-input-table-row px-2 rounded-md bg-[#181818] w-full h-[28px] ${errors?.type_object_purchase ? 'border border-[#C15C5C]' : 'border-0'}`}
                            value={
                                plannedAfterSecondYear
                                    ? ''
                                    : (positionRow.type_object_purchase ?? '')
                            }
                            onChange={(e) =>
                                onTypePurchaseChanged(e.target.value)
                            }
                        >
                            <option
                                className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                value=""
                            >
                                {plannedAfterSecondYear ? '' : 'Выбрать'}
                            </option>
                            <option value="G">Товар</option>
                            <option value="W">Работа</option>
                            <option value="S">Услуга</option>
                        </select>
                    </HoverTooltip>
                </div>
            </fieldset>

            <div className="relative flex flex-row h-[28px] w-28 items-center justify-end">
                <HoverTooltip
                    tooltipText={
                        errors?.okpd2_code
                            ? errors?.okpd2_code
                            : positionRow.okpd2_code
                              ? `${positionRow.okpd2_code} - ${positionRow.okpd2_name}`
                              : `Пусто`
                    }
                >
                    <div>
                        <input
                            ref={okpd2InputRef}
                            data-autocomplete="okpd2"
                            type="text"
                            className={`form-input-table-row flex pl-2 pr-9 w-full h-[28px] rounded-md bg-[#181818] text-[#181818] dark:text-[#FFFFFF] ${
                                errors?.okpd2_code
                                    ? 'border border-[#C15C5C]'
                                    : 'border-0'
                            }`}
                            value={okpd2Query}
                            onChange={(e) => {
                                okpd2PickedRef.current = false
                                if (disabledFieldset) return
                                onOkpd2Input(e.target.value.replace(',', '.'))
                            }}
                            onFocus={() => {
                                okpd2PrevRef.current = okpd2Query
                                okpd2PickedRef.current = false
                                if (okpd2Items.length) setOkpd2Open(true)
                                setOkved2Open(false)
                            }}
                            disabled={disabledFieldset}
                        />
                    </div>
                </HoverTooltip>

                <Okpd2Dropdown
                    anchorEl={okpd2InputRef.current}
                    open={okpd2Open}
                    onClose={() => {
                        setOkpd2Open(false)
                        if (!okpd2PickedRef.current) {
                            setOkpd2Query(okpd2PrevRef.current)
                        }
                    }}
                    items={okpd2Items}
                    theme={theme}
                    onPick={(it) => {
                        // ← сюда придёт выбранный элемент
                        okpd2PickedRef.current = true
                        okpd2PrevRef.current = it.code
                        setOkpd2Open(false)
                        setOkpd2Query(it.code)
                        onPickOkpd2(index, it) // ваш коллбек в родителе
                    }}
                    posMSPStatus={posMSPStatus}
                    purchaseMethodCode={purchaseMethodCode}
                    isFirstSelected={isFirstSelected}
                    rowsCount={rowsCount}
                    orgDataIsComplianceSmb={orgData?.is_compliance_smb ?? false}
                    orgDataIgnoreMspOkpd2={orgData?.ignore_msp_okpd2 ?? true}
                    orgDataAllOkpd2={orgData?.all_okpd2 ?? false}
                />

                {!disabledFieldset && (
                    <HoverTooltip tooltipText="Открыть справочник ОКПД2">
                        <button
                            className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center bg-[#FFFFFF] hover:bg-[#e4e6e8] dark:bg-[#181818] dark:hover:bg-[#656365]"
                            onClick={() => onDropdownSelected('okpd2')}
                        >
                            {/* иконка */}
                            <svg
                                className="w-3 h-3"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="bars"
                            >
                                <path
                                    fill={
                                        theme == 'dark' ? '#FFFFFF' : '#181818'
                                    }
                                    d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                ></path>
                            </svg>
                        </button>
                    </HoverTooltip>
                )}
            </div>

            <div
                id="okved2-autocomplete"
                className="relative flex flex-row h-[28px] w-28 items-center justify-end"
            >
                <HoverTooltip
                    tooltipText={
                        errors?.okved2_code
                            ? errors?.okved2_code
                            : positionRow.okved2_code
                              ? `${positionRow.okved2_code} - ${positionRow.okved2_name}`
                              : `Пусто`
                    }
                >
                    <div>
                        <input
                            ref={okved2InputRef}
                            data-autocomplete="okved2"
                            type="text"
                            className={`form-input-table-row flex pl-2 pr-9 w-full h-[28px] rounded-md bg-[#181818] text-[#181818] dark:text-[#FFFFFF] ${
                                errors?.okved2_code
                                    ? 'border border-[#C15C5C]'
                                    : 'border-0'
                            }`}
                            value={okved2Query}
                            onChange={(e) => {
                                okved2PickedRef.current = false
                                if (disabledFieldset) return
                                onOkved2Input(e.target.value.replace(',', '.'))
                            }}
                            onFocus={() => {
                                okved2PrevRef.current = okved2Query
                                okved2PickedRef.current = false
                                if (okved2Items.length) setOkved2Open(true)
                            }}
                            disabled={disabledFieldset}
                        />
                    </div>
                </HoverTooltip>

                {!disabledFieldset && (
                    <HoverTooltip tooltipText="Открыть справочник ОКВЭД2">
                        <button
                            className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center bg-[#FFFFFF] hover:bg-[#e4e6e8] dark:bg-[#181818] dark:hover:bg-[#656365]"
                            onClick={() => onDropdownSelected('okved2')}
                        >
                            <svg
                                className="w-3 h-3"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="bars"
                            >
                                <path
                                    fill={
                                        theme == 'dark' ? '#FFFFFF' : '#181818'
                                    }
                                    d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                ></path>
                            </svg>
                        </button>
                    </HoverTooltip>
                )}
                <FixedDropdown
                    anchorEl={okved2InputRef.current}
                    open={okved2Open}
                    onClose={() => {
                        setOkved2Open(false)
                        if (!okved2PickedRef.current) {
                            setOkved2Query(okved2PrevRef.current)
                        }
                    }}
                    minWidth={400}
                    maxHeight={250}
                    closeOnInsidePointerDown
                >
                    <div className="py-1">
                        {okved2Items.map((it) => {
                            const pick = () => {
                                // без проверок — выбираем сразу
                                okved2PickedRef.current = true
                                okved2PrevRef.current = it.code
                                setOkved2Open(false)
                                pickOkved2(it)
                            }
                            return (
                                <button
                                    key={it.code}
                                    className={`w-full text-left px-2 py-1 text-[11px] hover:bg-[#e4e6e8] dark:hover:bg-[#373538] text-[#181818] dark:text-[#FFFFFF] cursor-pointer`}
                                    onPointerDown={(e) => {
                                        e.preventDefault() // не даём фокусам/blur закрыть список
                                        e.stopPropagation() // чтобы вне-клик не сработал
                                        pick()
                                    }}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="shrink-0">
                                            {it.code}
                                        </span>
                                        <span className="whitespace-pre-wrap">
                                            {it.name}
                                        </span>
                                    </div>
                                </button>
                            )
                        })}
                        {okved2Items.length === 0 && (
                            <div className="px-2 py-2 text-[#6A6A6A] dark:text-[#8A8A8A] text-[11px]">
                                Ничего не найдено
                            </div>
                        )}
                    </div>
                </FixedDropdown>
            </div>

            {showRegion && (
                <div className="relative flex flex-row w-44 h-[28px] items-center justify-end">
                    <HoverTooltip
                        tooltipText={
                            errors?.okato
                                ? errors?.okato
                                : `${positionRow.okato} - ${positionRow.region}`
                        }
                    >
                        <input
                            type="text"
                            className={`form-input-table-row flex pl-2 pr-9 w-full h-[28px] rounded-md bg-[#181818] text-[#181818] dark:text-[#FFFFFF] ${errors?.okato ? 'border border-[#C15C5C]' : 'border-0'}`}
                            value={positionRow.region ?? ''}
                            readOnly
                        />
                    </HoverTooltip>

                    <button
                        className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center bg-[#FFFFFF] hover:bg-[#e4e6e8] dark:bg-[#181818] dark:hover:bg-[#656365]"
                        onClick={() => onDropdownSelected('okato')}
                    >
                        <svg
                            className="w-3 h-3"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="bars"
                        >
                            <path
                                fill={theme == 'dark' ? '#FFFFFF' : '#181818'}
                                d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                            ></path>
                        </svg>
                    </button>
                </div>
            )}

            <div className="flex flex-row h-8 w-fit items-center justify-center text-center gap-1">
                <div className=" flex w-6 h-5 items-center justify-center pl-1 border-l border-[#b8b8b8] dark:border-[#464447]">
                    {dataSearchOkpd2?.is_msp ? (
                        <HoverTooltip tooltipText="Закупка только у субъектов МСП">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#187A33"
                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : (
                        ''
                    )}
                </div>

                <div className=" flex w-6 h-5 items-center justify-center">
                    {dataSearchOkpd2?.is_rf_ban &&
                    dataSearchOkpd2?.is_rf_limit ? (
                        <HoverTooltip
                            tooltipText={`${dataSearchOkpd2?.is_rf_ban && dataSearchOkpd2?.is_rf_limit ? `Запрет + Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_ban ? `Запрет ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_limit ? `Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_quota ? `Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}`}`}`}`}
                        >
                            <svg
                                className="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#EF4444"
                                    d="M16.5303 6.46967C16.8232 6.76256 16.8232 7.23744 16.5303 7.53033L6.53033 17.5303C6.38968 17.671 6.19891 17.75 6 17.75 5.80109 17.75 5.61032 17.671 5.46967 17.5303L1.46967 13.5303C1.17678 13.2374 1.17678 12.7626 1.46967 12.4697 1.76256 12.1768 2.23744 12.1768 2.53033 12.4697L6 15.9393 15.4697 6.46967C15.7626 6.17678 16.2374 6.17678 16.5303 6.46967zM22.5303 6.46966C22.8232 6.76254 22.8232 7.23742 22.5303 7.53032L12.5308 17.5303C12.2379 17.8232 11.7631 17.8232 11.4702 17.5304L9.96975 16.0304C9.67681 15.7376 9.67674 15.2627 9.96959 14.9697 10.2624 14.6768 10.7373 14.6767 11.0303 14.9696L12.0004 15.9394 21.4697 6.46968C21.7625 6.17678 22.2374 6.17677 22.5303 6.46966z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : dataSearchOkpd2?.is_rf_ban ? (
                        <HoverTooltip
                            tooltipText={`${dataSearchOkpd2?.is_rf_ban && dataSearchOkpd2?.is_rf_limit ? `Запрет + Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_ban ? `Запрет ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_limit ? `Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_quota ? `Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}`}`}`}`}
                        >
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#EF4444"
                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : dataSearchOkpd2?.is_rf_limit ? (
                        <HoverTooltip
                            tooltipText={`${dataSearchOkpd2?.is_rf_ban && dataSearchOkpd2?.is_rf_limit ? `Запрет + Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_ban ? `Запрет ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_limit ? `Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_quota ? `Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}`}`}`}`}
                        >
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#FAB502"
                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : dataSearchOkpd2?.is_rf_quota ? (
                        <HoverTooltip
                            tooltipText={`${dataSearchOkpd2?.is_rf_ban && dataSearchOkpd2?.is_rf_limit ? `Запрет + Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_ban ? `Запрет ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_limit ? `Ограничение ${dataSearchOkpd2?.is_rf_quota ? `+ Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}` : `${dataSearchOkpd2?.is_rf_quota ? `Доля РФ (${dataSearchOkpd2?.quota_percent}%)` : ``}`}`}`}`}
                        >
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#187A33"
                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : (
                        ''
                    )}
                </div>

                <div className=" flex w-6 h-5 items-center justify-center">
                    {dataSearchOkpd2?.is_electronic == true ? (
                        <HoverTooltip tooltipText="Закупка только в электронной форме">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="check"
                            >
                                <path
                                    fill="#187A33"
                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                ></path>
                            </svg>
                        </HoverTooltip>
                    ) : (
                        ''
                    )}
                </div>
            </div>

            {!disabledFieldset && (
                <div className="flex flex-row h-8 w-8 items-center justify-center text-center">
                    <HoverTooltip tooltipText="Удалить строку">
                        <button
                            onClick={(e) => onDeleteClick()}
                            className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                        >
                            <svg
                                className="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="trash-alt"
                            >
                                <path
                                    fill={
                                        theme == 'dark' ? '#FFFFFF' : '#181818'
                                    }
                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                ></path>
                            </svg>
                        </button>
                    </HoverTooltip>
                </div>
            )}
        </div>
    )
}

export default PurchaseItemsTableRow
